import { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import { useSnackbar } from 'notistack';
// @mui
import {
  Drawer,
  Typography,
  Stack,
  Box,
  Divider,
  Avatar,
  Badge,
  Fade,
  IconButton,
  CircularProgress,
  Tab,
  Tabs,
} from '@mui/material';
import { styled } from "@mui/material/styles";
// components
import Label from '../../components/Label';
import Image from "../../components/Image";
import Iconify from '../../components/Iconify';
import {
  InstantActionButton,
  CrewInfoTabPanel,
  ProgressPanel,
  MessagePanel,
  ContinuePanel,
  MeetingConfirmDialog,
  ParttimeOfferDialog,
} from './coffeechat';
// hooks
import useAuth from '../../hooks/useAuth';
// util
import axios from '../../utils/axios';
import { fDotDateKST } from '../../utils/formatTime';
// routes
import { PATH_JOBS } from '../../routes/paths';

const PrimaryText = styled('span')(({ theme }) => {
  return {
    color: theme.palette.text.primary,
  };
});

export default function FliersDetailDrawer({ isOpen, closeDrawer, coffeeChat, reload, reloadScout }) {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [tabValue, setTabValue] = useState("crew");
  const [showHeader, setShowHeader] = useState(true);
  const router = useRouter();
  const { enqueueSnackbar } = useSnackbar();

  const scoutDeclined = coffeeChat?.company_decline_reason?.length > 0;
  const paidPlan = user?.employee?.company?.[0]?.scout_plan === 'enterprise' || user?.employee?.company?.[0]?.scout_plan === 'pro';

  // dialogs
  const [isOpenMeetingConfirmDialog, setIsOpenMeetingConfirmDialog] = useState(false);
  const [isOpenCheckMeetingSchedule, setIsOpenCheckMeetingSchedule] = useState(false);
  const [isOpenParttimeOfferDialog, setIsOpenParttimeOfferDialog] = useState(false);
  const [isOpenSendMessage, setIsOpenSendMessage] = useState(false);
  const [isOpenReadMessage, setIsOpenReadMessage] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [isOpenOfferMessage, setIsOpenOfferMessage] = useState(false);
  const [isOpenContinueMessage, setIsOpenContinueMessage] = useState(false);
  const [isOpenContinueWork, setIsOpenContinueWork] = useState(false);

  const loadCrewProfile = async () => {
    try {
      const companyId = user?.employee?.company?.[0].id;
      setIsLoading(true);
      const response = await axios.get(`/api/company/${companyId}/fliers/${coffeeChat?.crew?.id}/`);
      setProfile(response.data || null);
      setIsLoading(false);
    } catch (err) {
      console.error(err);
      setIsLoading(false);
    }
  };

  const openMeetingConfirmDialog = () => {
    setIsOpenMeetingConfirmDialog(true);
  };

  const closeMeetingConfirmDialog = () => {
    setIsOpenMeetingConfirmDialog(false);
    reload();
  };

  const openParttimeOfferDialog = () => {
    setIsOpenParttimeOfferDialog(true);
  };

  const closeParttimeOfferDialog = () => {
    setIsOpenParttimeOfferDialog(false);
  };

  const openOfferMessage = () => {
    setTabValue("progress");
    setShowHeader(false);
    setIsOpenOfferMessage(true);
  };

  const closeOfferMessage = () => {
    if (["wait", "stop", "company-decline"].includes(coffeeChat?.coffee_chat_status)) {
      setTabValue("crew");
    }
    setShowHeader(true);
    setIsOpenOfferMessage(false);
  };

  const openSendMessage = () => {
    setTabValue("message");
    setShowHeader(false);
    setIsOpenSendMessage(true);
  };

  const closeSendMessage = () => {
    setShowHeader(true);
    setIsOpenSendMessage(false);
  };

  const openCheckMeetingSchedule = () => {
    setTabValue("progress");
    setShowHeader(false);
    setIsOpenCheckMeetingSchedule(true);
  };

  const closeCheckMeetingSchedule = () => {
    setShowHeader(true);
    setIsOpenCheckMeetingSchedule(false);
  };

  const openReadMessage = (message) => {
    setShowHeader(false);
    setTabValue("message");
    setSelectedMessage(message);
    setIsOpenReadMessage(true);
  };

  const closeReadMessage = () => {
    setShowHeader(true);
    setIsOpenReadMessage(false);
    setSelectedMessage(null);
  };

  const openContinueMessage = () => {
    setTabValue("continue");
    setShowHeader(false);
    setIsOpenContinueMessage(true);
  };

  const closeContinueMessage = () => {
    setShowHeader(true);
    setIsOpenContinueMessage(false);
  };

  const moveToContinueTab = () => {
    setTabValue("continue");
  };

  const openContinueWork = () => {
    if (profile?.ongoing_offer_id) {
      enqueueSnackbar("이미 업무 제안을 보냈습니다.", { variant: 'warning' });
      router.push({pathname: PATH_JOBS.root, query: {jid: profile?.ongoing_offer_id }});
    } else {
      setTabValue("continue");
      setShowHeader(false);
      setIsOpenContinueWork(true);
    }
  };

  const closeContinueWork = () => {
    setShowHeader(true);
    setIsOpenContinueWork(false);
  };

  const resetPanel = () => {
    setShowHeader(true);
    setIsOpenMeetingConfirmDialog(false);
    setIsOpenCheckMeetingSchedule(false);
    setIsOpenParttimeOfferDialog(false);
    setIsOpenSendMessage(false);
    setIsOpenReadMessage(false);
    setSelectedMessage(null);
    setIsOpenOfferMessage(false);
    setIsOpenContinueMessage(false);
    setIsOpenContinueWork(false);
  };

  useEffect(() => {
    if (isOpen) {
      setProfile(null);
      resetPanel();
      loadCrewProfile();
    }
  }, [isOpen]);

  useEffect(() => {
    if (coffeeChat?.coffee_chat_status === "meeting-done") {
      setTabValue("continue");
    } else {
      setTabValue("crew");
    }
  }, [coffeeChat]);

  return (
    <Drawer
      open={isOpen}
      onClose={closeDrawer}
      anchor="right"
      // hideBackdrop
      BackdropProps={{
        sx: {
          background: "none",
          backgroundColor: "transparent",
        }
      }}
      PaperProps={{
        sx: {
          width: 480,
          overscrollBehavior: "contain",
        }
      }}
      ModalProps={{
        keepMounted: false,
      }}
      disableScrollLock
    >
      {/* Content */}
      <Box
        sx={{ width: 1, p: 3 }}
        role="presentation"
      >
        {showHeader && (
        <Box sx={{ justifyContent: "flex-end", display: "flex", mb: 1 }}>
          <IconButton edge="end" color="inherit" onClick={closeDrawer}>
            <Iconify icon="eva:close-fill" sx={{ width: 28, height: 28, color: "text.secondary" }} />
          </IconButton>
        </Box>
        )}
        {showHeader && (
        <Fade in={profile && !!profile.id}>
        <Box
          sx={{ position: "sticky", pt: 3, top: 0, backgroundColor: "common.white", zIndex: 10 }}
        >
          <Stack direction="row" justifyContent="space-between">
            <Stack direction={"row"} spacing={2} sx={{ width: 1, overflow: "hidden" }} justifyContent="space-between">
              <Box sx={{ width: 1 }}>
                <Stack spacing={1}>
                  <Stack>
                    <Box>
                      {profile?.main_group && (
                      <Label variant="outlined" color="primary">
                        {profile?.main_group}
                      </Label>
                      )}
                    </Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant={"h3"}>
                        {profile?.main_role || ''}
                      </Typography>
                      <Typography variant={"h5"} color="text.secondary">
                        {profile?.position_display}
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center" divider={<Divider orientation={"vertical"} flexItem />}>
                      <Typography variant="subtitle1" color="text.secondary">
                        <PrimaryText>{profile?.nickname}</PrimaryText> 크루
                      </Typography>
                      <Typography variant="subtitle1" color="text.secondary">
                        {profile?.years}년차
                      </Typography>
                    </Stack>
                  </Stack>
                </Stack>
              </Box>
              <Box>
                <Badge
                  sx={{
                    width: 56,
                    height: 56,
                  }}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  badgeContent={
                    <Image src={"https://spacey-dio-public.s3.ap-northeast-2.amazonaws.com/img_verified.png"} sx={{ width: 20, height: 20, mr: 2, mb: 2.5 }} />
                  }
                >
                  <Avatar
                    src={`https://spacey-dio-public.s3.ap-northeast-2.amazonaws.com/crewji/crewji_${profile?.randomhash || '5'}.png`}
                    alt={profile?.name}
                    color={'default'}
                    sx={{
                      margin: 'auto',
                      borderWidth: 2,
                      borderStyle: 'solid',
                      borderColor: 'common.white',
                      width: 56,
                      height: 56,
                    }}
                  />
                </Badge>
              </Box>
            </Stack>
          </Stack>

          {/* action by step */}
          {scoutDeclined ? (
          <Box
            sx={{
              py: 1.5,
              px: 2,
              borderRadius: 1,
              mt: 2,
              bgcolor: "background.neutral",
            }}
          >
            <Stack direction="row" alignItems={"center"} spacing={1} justifyContent={"space-between"} sx={{ mb: 0.5 }}>
              <Typography variant="subtitle2">
                인재풀에 추가하지 않은 이유
              </Typography>
              <Typography sx={{ color: "text.disabled" }} variant="caption">
                {fDotDateKST(coffeeChat?.company_declined_at)}
              </Typography>
            </Stack>
            <Typography variant="body2" sx={{ color: "text.secondary", whiteSpace: "pre-wrap" }}>
              {coffeeChat?.company_decline_reason}
            </Typography>
          </Box>
          ) : (
          <InstantActionButton
            profile={profile}
            coffeeChat={coffeeChat}
            reload={reload}
            reloadScout={reloadScout}
            openOfferMessage={openOfferMessage}
            openMeetingConfirmDialog={openMeetingConfirmDialog}
            openCheckMeetingSchedule={openCheckMeetingSchedule}
            openParttimeOfferDialog={openParttimeOfferDialog}
            openMessagePanel={openSendMessage}
          />
          )}

          {/* tabs */}
          <Box
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              mt: 1,
              mb: 3,
            }}
          >
            <Tabs
              value={tabValue}
              onChange={(e, v) => setTabValue(v)}
              aria-label="coffee chat tabs"
              indicatorColor="primary"
              textColor="inherit"
              variant="standard"
              sx={{
                "& .MuiTab-root:not(:last-of-type)": {
                  mr: 3,
                }
              }}
            >
              {paidPlan && coffeeChat?.coffee_chat_status && !["dio-will-offer", "suggestable", "evaluable", "wait", "stop", "company-decline"].includes(coffeeChat?.coffee_chat_status) && (
                <Tab value={"progress"} label="진행 단계" id="coffee-chat-tab-progress" aria-controls="coffee-chat-tabpanel-progress" />
              )}
              <Tab value={"crew"} label="크루 정보" id="coffee-chat-tab-info" aria-controls="coffee-chat-tabpanel-info" />
              {coffeeChat?.coffee_chat_status && !["dio-will-offer", "suggestable","evaluable", "wait"].includes(coffeeChat?.coffee_chat_status) && (
                <Tab value={"message"} label="메시지" id="coffee-chat-tab-message" aria-controls="coffee-chat-tabpanel-message" />
              )}
              {coffeeChat?.coffee_chat_status && ["meeting-done"].includes(coffeeChat?.coffee_chat_status) && (
                <Tab value={"continue"} label="이어가기" id="coffee-chat-tab-continue" aria-controls="coffee-chat-tabpanel-continue" />
              )}
            </Tabs>
          </Box>
        </Box>
        </Fade>
        )}

        {isLoading && (
          <Box sx={{ height: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <CircularProgress sx={{ color: 'secondary.main' }} />
          </Box>
        )}

        {!isLoading && tabValue === "crew" && (<CrewInfoTabPanel profile={profile} coffeeChat={coffeeChat} />)}
        {!isLoading && paidPlan && tabValue === "progress" && (
          <ProgressPanel
            coffeeChat={coffeeChat}
            isOpenOfferMessage={isOpenOfferMessage}
            openOfferMessage={openOfferMessage}
            closeOfferMessage={closeOfferMessage}
            openMeetingConfirmDialog={openMeetingConfirmDialog}
            isOpenCheckMeetingSchedule={isOpenCheckMeetingSchedule}
            openCheckMeetingSchedule={openCheckMeetingSchedule}
            closeCheckMeetingSchedule={closeCheckMeetingSchedule}
            moveToContinueTab={moveToContinueTab}
          />
        )}
        {!isLoading && tabValue === "message" && (
          <MessagePanel
            coffeeChat={coffeeChat}
            isOpenSendMessage={isOpenSendMessage}
            openSendMessage={openSendMessage}
            closeSendMessage={closeSendMessage}
            isOpenReadMessage={isOpenReadMessage}
            selectedMessage={selectedMessage}
            openReadMessage={openReadMessage}
            closeReadMessage={closeReadMessage}
          />
        )}
        {!isLoading && tabValue === "continue" && (
          <ContinuePanel
            reload={reload}
            coffeeChat={coffeeChat}
            closedMatchesCount={profile?.closed_matches_count}
            moveToMessageTab={() => {closeContinueMessage(); setTabValue("message")}}
            isOpenContinueMessage={isOpenContinueMessage}
            openContinueMessage={openContinueMessage}
            closeContinueMessage={closeContinueMessage}
            isOpenContinueWork={isOpenContinueWork}
            openContinueWork={openContinueWork}
            closeContinueWork={closeContinueWork}
          />
        )}

      </Box>

      <MeetingConfirmDialog
        isOpen={isOpenMeetingConfirmDialog}
        closeDialog={closeMeetingConfirmDialog}
        coffeeChat={coffeeChat}
      />
      <ParttimeOfferDialog
        isOpen={isOpenParttimeOfferDialog}
        closeDialog={closeParttimeOfferDialog}
        coffeeChat={coffeeChat}
        moveToContinueWork={() => {closeParttimeOfferDialog(); openContinueWork(); }}
      />
    </Drawer>
  )
}